import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Briefcase, GraduationCap, Calendar, CheckCircle, AlertCircle } from 'lucide-react';
import { Profile } from '../../types';
import { useAuth } from '../../hooks/useAuth';

interface ProfileCardProps {
  profile: Profile;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ profile }) => {
  const { user } = useAuth();
  
  const getVerificationBadge = () => {
    switch (profile.verificationStatus) {
      case 'verified':
        return (
          <div className="flex items-center space-x-1 text-green-600 text-xs">
            <CheckCircle className="w-3 h-3" />
            <span>Verified</span>
          </div>
        );
      case 'semi-verified':
        return (
          <div className="flex items-center space-x-1 text-yellow-600 text-xs">
            <AlertCircle className="w-3 h-3" />
            <span>Semi-verified</span>
          </div>
        );
      default:
        return null;
    }
  };

  const formatLocation = () => {
    if (profile.workLocation?.type === 'nri') {
      return profile.workLocation.country?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'NRI';
    }
    const city = profile.workLocation?.city?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
    const state = profile.workLocation?.state?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
    return `${city}, ${state}`;
  };

  const formatEducationOccupation = () => {
    const education = profile.education?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
    const occupation = profile.occupation?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
    return `${education} • ${occupation}`;
  };

  const getInitials = () => {
    return profile.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'NA';
  };

  const canViewFullProfile = user?.verificationStatus === 'verified';

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="p-6">
        {/* Profile Image/Initials */}
        <div className="flex items-start space-x-4 mb-4">
          <div className="flex-shrink-0">
            {profile.photos && profile.photos.length > 0 && canViewFullProfile ? (
              <img
                src={profile.photos[0]}
                alt={profile.name}
                className="w-16 h-16 rounded-lg object-cover"
              />
            ) : profile.profilePhoto && canViewFullProfile ? (
              <img
                src={profile.profilePhoto}
                alt={profile.name}
                className="w-16 h-16 rounded-lg object-cover"
              />
            ) : (
              <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center">
                <span className="text-primary-600 font-semibold text-lg">
                  {getInitials()}
                </span>
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {profile.name}
              </h3>
              {getVerificationBadge()}
            </div>
            
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <Calendar className="w-4 h-4 mr-1" />
              <span>{profile.age} years</span>
              <span className="mx-2">•</span>
              <span className="capitalize">{profile.height?.replace('.', '\'')}"</span>
            </div>
            
            <div className="flex items-center text-sm text-gray-600">
              <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
              <span className="truncate">{formatLocation()}</span>
            </div>
          </div>
        </div>

        {/* Education & Occupation */}
        <div className="flex items-center text-sm text-gray-600 mb-3">
          <GraduationCap className="w-4 h-4 mr-1 flex-shrink-0" />
          <span className="truncate">{formatEducationOccupation()}</span>
        </div>

        {/* Caste & Gothra */}
        <div className="text-sm text-gray-600 mb-3">
          <span className="capitalize">{profile.caste}</span>
          {profile.subcaste && <span> • {profile.subcaste}</span>}
          {profile.gothra && <span> • {profile.gothra}</span>}
        </div>

        {/* Marital Status & Income */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-gray-600 capitalize">
            {profile.maritalStatus?.replace('-', ' ')}
          </span>
          <span className="text-sm text-gray-600">
            {profile.annualIncome?.replace(' ', '-')} LPA
          </span>
        </div>

        {/* Self Summary Preview */}
        {profile.selfSummary && (
          <p className="text-sm text-gray-700 mb-4 line-clamp-2">
            {profile.selfSummary.length > 100 
              ? `${profile.selfSummary.substring(0, 100)}...` 
              : profile.selfSummary
            }
          </p>
        )}

        {/* Action Button */}
        <div className="flex space-x-3">
          <Link
            to={`/profile/${profile.id}`}
            className="flex-1 bg-primary-600 text-white text-center py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            View Profile
          </Link>
          
          {canViewFullProfile && (
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm">
              Interest
            </button>
          )}
        </div>

        {/* Verification Notice */}
        {!canViewFullProfile && (
          <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-700 text-center">
              Complete verification to view photos and contact details
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileCard;